/**
 * 单聊「更多」：右侧抽屉。
 * - 展示资料（头像 / 昵称 / 备注 / userID）
 * - 操作：设备注、拉入群、删好友、拉黑
 * - 拉入群聊：选已加入的群 → inviteUserToGroup（不要求先加好友）
 */
import { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Drawer,
  Flex,
  Input,
  Modal,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import type { FriendProfile, JoinedGroupBrief } from "../types";

type Props = {
  /** 当前操作的对方 OpenIM userID；空则关抽屉 */
  userID: string | null;
  open: boolean;
  onClose: () => void;
  /** 打开时拉资料 */
  loadProfile: (userID: string) => Promise<FriendProfile>;
  /** 拉已加入群（给下拉用） */
  loadGroups: () => Promise<JoinedGroupBrief[]>;
  setRemark: (userID: string, remark: string) => Promise<void>;
  deleteFriend: (userID: string) => Promise<void>;
  addBlack: (userID: string) => Promise<void>;
  inviteToGroup: (groupID: string, userID: string) => Promise<void>;
  /** 删好友 / 拉黑成功后，父级可刷新会话列表 */
  onChanged?: () => void;
};

function initialOf(name: string): string {
  const t = (name || "").trim();
  return t ? t.slice(0, 1) : "?";
}

export function FriendActionsDrawer({
  userID,
  open,
  onClose,
  loadProfile,
  loadGroups,
  setRemark,
  deleteFriend,
  addBlack,
  inviteToGroup,
  onChanged,
}: Props) {
  const [profile, setProfile] = useState<FriendProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // 备注编辑
  const [remarkDraft, setRemarkDraft] = useState("");
  const [remarkOpen, setRemarkOpen] = useState(false);

  // 拉入群聊
  const [inviteOpen, setInviteOpen] = useState(false);
  const [groups, setGroups] = useState<JoinedGroupBrief[]>([]);
  const [groupID, setGroupID] = useState<string>();

  // 打开抽屉或换人时重新拉资料
  useEffect(() => {
    if (!open || !userID) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void loadProfile(userID)
      .then((p) => {
        if (!cancelled) {
          setProfile(p);
          setRemarkDraft(p.remark || "");
        }
      })
      .catch((e: Error) => {
        if (!cancelled) message.error(e.message || String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userID, loadProfile]);

  const run = async (fn: () => Promise<void>, okText: string) => {
    if (!userID || busy) return;
    setBusy(true);
    try {
      await fn();
      message.success(okText);
      onChanged?.();
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const titleName =
    profile?.remark || profile?.nickname || userID || "好友资料";

  return (
    <>
      <Drawer
        title="好友资料"
        open={open}
        onClose={onClose}
        width={360}
        destroyOnClose
      >
        {loading || !profile ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : (
          <Flex vertical gap={16}>
            {/* —— 信息区 —— */}
            <Flex vertical align="center" gap={8}>
              <Avatar
                size={72}
                src={profile.faceURL || undefined}
                style={{ background: "#1e293b" }}
              >
                {initialOf(titleName)}
              </Avatar>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {titleName}
              </Typography.Title>
              {profile.remark ? (
                <Typography.Text type="secondary">
                  昵称：{profile.nickname}
                </Typography.Text>
              ) : null}
              <Typography.Text type="secondary" copyable>
                {profile.userID}
              </Typography.Text>
              <Typography.Text type="secondary">
                {profile.isFriend ? "好友" : "未知"}
              </Typography.Text>
            </Flex>

            {/* —— 操作区 —— */}
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                block
                disabled={!profile.isFriend || busy}
                onClick={() => {
                  setRemarkDraft(profile.remark || "");
                  setRemarkOpen(true);
                }}
              >
                设置备注
              </Button>

              <Button
                block
                disabled={busy}
                onClick={() => {
                  setGroupID(undefined);
                  setInviteOpen(true);
                  // 打开时拉群列表
                  void loadGroups()
                    .then(setGroups)
                    .catch((e: Error) => message.error(e.message));
                }}
              >
                拉入群聊
              </Button>

              <Button
                block
                danger
                disabled={!profile.isFriend || busy}
                onClick={() => {
                  Modal.confirm({
                    title: "删除好友",
                    content: `确定删除 ${titleName}？删除后需重新申请才能聊天（会话可能仍在列表）。`,
                    okType: "danger",
                    onOk: () =>
                      run(async () => {
                        await deleteFriend(profile.userID);
                        onClose();
                      }, "已删除好友"),
                  });
                }}
              >
                删除好友
              </Button>

              <Button
                block
                danger
                disabled={busy}
                onClick={() => {
                  Modal.confirm({
                    title: "拉黑",
                    content: `确定拉黑 ${titleName}？拉黑后对方消息会被拦截。`,
                    okType: "danger",
                    onOk: () =>
                      run(async () => {
                        await addBlack(profile.userID);
                        onClose();
                      }, "已拉黑"),
                  });
                }}
              >
                拉黑好友
              </Button>
            </Space>
          </Flex>
        )}
      </Drawer>

      {/* 设置备注 */}
      <Modal
        title="设置备注"
        open={remarkOpen}
        onCancel={() => setRemarkOpen(false)}
        confirmLoading={busy}
        onOk={() => {
          if (!userID) return;
          void run(async () => {
            await setRemark(userID, remarkDraft);
            setRemarkOpen(false);
            // 本地立刻反映
            setProfile((p) => (p ? { ...p, remark: remarkDraft.trim() } : p));
          }, "备注已更新");
        }}
      >
        <Input
          value={remarkDraft}
          maxLength={64}
          placeholder="备注名"
          onChange={(e) => setRemarkDraft(e.target.value)}
        />
      </Modal>

      {/* 拉入群聊：选一个已加入的群 */}
      <Modal
        title="拉入群聊"
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        confirmLoading={busy}
        okButtonProps={{ disabled: !groupID }}
        onOk={() => {
          if (!userID || !groupID) return;
          void run(async () => {
            await inviteToGroup(groupID, userID);
            setInviteOpen(false);
          }, "已发送入群邀请");
        }}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          不要求对方先同意加好友；若群开启了入群验证，对方仍可能要审批。
        </Typography.Paragraph>
        <Select
          style={{ width: "100%" }}
          placeholder="选择群"
          value={groupID}
          onChange={setGroupID}
          options={groups.map((g) => ({
            value: g.groupID,
            label: `${g.groupName}（${g.memberCount}人）`,
          }))}
        />
      </Modal>
    </>
  );
}